/**
 * The calendars on a phone — `docs/new-request.md` §16.3, Boards 15 C–D, 16 A.
 *
 * Three screens over the form, each *Back · <title> · Done*:
 *
 * - **<first name>’s two weeks** — Best times stacked, *From today · nearest
 *   stop* as a sideways strip; a day opens its hours.
 * - **<Dow Mon d>** — the day's best time as a chip, the same timeline full
 *   width, the map one tap away.
 * - **Side by side** — each person's best time stacked, then the nearness
 *   grid six days wide (*swipe for more days*); a cell opens that person's day.
 *
 * A pick is this screen's until **Done** puts *Who goes* and *When* on the
 * form; **Back** from the first screen leaves the form as it was.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  GRADE_LABELS, clock, compareOpenings, dayShort, gapsOf, placementOf, type CalendarPerson,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { instantOf, timeOf, useRequestSheet, type ChosenTime, type PersonRef } from '../sheet-context.js'
import { BestTimeCard, EstimatesBand, PersonBestCard, notCoveringWords } from './bits.js'
import { DayMap } from './day-map.js'
import { DayTimeline, axisOf, blockWords } from './day-timeline.js'
import { PinGlyph } from './glyphs.js'
import { PlacementBand, useEscapeCloses } from './panes.js'
import { PhoneNearnessGrid } from './side-by-side.js'
import { PhoneStrip } from './strip.js'
import { useCalendar, useChoice, useCompare, type Choice } from './use-schedule.js'

type Screen =
  | { kind: 'two' }
  | { kind: 'side' }
  | { kind: 'day'; person: PersonRef; date: string; back: 'two' | 'side' }

const refOf = (person: CalendarPerson | PersonRef): PersonRef =>
  ({ id: person.id, name: person.name, firstName: person.firstName })

function PhoneHeader({ title, onBack, onDone }: { title: string; onBack: () => void; onDone: () => void }) {
  return (
    <div className="flex h-[52px] shrink-0 items-center border-b border-line bg-surface px-3.5">
      <button type="button" onClick={onBack} className="w-[70px] text-left text-[15px] font-medium text-accent">
        Back
      </button>
      <span className="flex-1" />
      <b className="truncate text-[15.5px] font-bold text-text">{title}</b>
      <span className="flex-1" />
      <button type="button" onClick={onDone} className="w-[70px] text-right text-[15px] font-bold text-accent">
        Done
      </button>
    </div>
  )
}

const PhoneLabel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('text-[11px] font-semibold uppercase tracking-[.06em] text-text-faint', className)}>
    {children}
  </div>
)

const Body = ({ children, padded = 'px-4 py-3.5' }: { children: ReactNode; padded?: string }) => (
  <div className={cn('min-h-0 flex-1 overflow-y-auto', padded)}>{children}</div>
)

const Quiet = ({ failed }: { failed: boolean }) => (
  <div className="py-6 text-center text-[13px] text-text-muted">
    {failed ? 'The calendar didn’t load — try again in a moment.' : 'Finding openings…'}
  </div>
)

export function PhoneSchedule() {
  const api = useRequestSheet()
  const root = useRef<HTMLDivElement>(null)
  const choice = useChoice('pending')
  const schedule = api.schedule
  const [screen, setScreen] = useState<Screen>(() => (schedule?.mode === 'side' ? { kind: 'side' } : { kind: 'two' }))
  useEscapeCloses(root, api.closeSchedule, Boolean(api.schedule))

  /* Opened again in the other mode while up (the form's *Side by side*): follow it. */
  const mode = schedule?.mode ?? null
  useEffect(() => {
    if (mode === 'side') setScreen((was) => (was.kind === 'two' ? { kind: 'side' } : was))
    if (mode === 'one') setScreen((was) => (was.kind === 'side' ? { kind: 'two' } : was))
  }, [mode])

  if (!schedule || schedule.people.length === 0) return null
  const done = () => { choice.commit(); api.closeSchedule() }

  return (
    <div ref={root} className="flex h-full min-h-0 flex-col bg-bg" data-schedule-phone={screen.kind}>
      {screen.kind === 'two' && (
        <TwoWeeks choice={choice} onDone={done}
          onDay={(person, date) => setScreen({ kind: 'day', person, date, back: 'two' })} />
      )}
      {screen.kind === 'side' && (
        <SideScreen choice={choice} onDone={done}
          onDay={(person, date) => setScreen({ kind: 'day', person, date, back: 'side' })} />
      )}
      {screen.kind === 'day' && (
        <DayScreen choice={choice} person={screen.person} date={screen.date} onDone={done}
          onBack={() => setScreen({ kind: screen.back })} />
      )}
    </div>
  )
}

/** Board 15 C. */
function TwoWeeks({ choice, onDay, onDone }: {
  choice: Choice
  onDay: (person: PersonRef, date: string) => void
  onDone: () => void
}) {
  const api = useRequestSheet()
  const schedule = api.schedule!
  const person = schedule.people[0]!
  const shown = choice.shown
  const wanted = schedule.day ?? (shown && shown.personId === person.id ? shown.date : null)
  const { view, failed } = useCalendar(person.id, wanted, schedule.first)
  const hasPlace = api.facts.place !== null

  return (
    <>
      <PhoneHeader title={`${person.firstName}’s two weeks`} onBack={api.closeSchedule} onDone={onDone} />
      <Body>
        {!view && <Quiet failed={failed} />}
        {view && (
          <>
            {view.estimates && hasPlace && <div className="mb-3"><EstimatesBand /></div>}
            {hasPlace && view.best.length > 0 && (
              <>
                <PhoneLabel className="mb-2">Best times</PhoneLabel>
                <div className="flex flex-col gap-2">
                  {view.best.map((opening) => (
                    <BestTimeCard
                      key={`${opening.date}-${opening.start}`}
                      opening={opening}
                      phone
                      selected={Boolean(shown && shown.personId === person.id && shown.date === opening.date
                        && shown.start === opening.start)}
                      onPick={() => choice.choose(person, timeOf(opening))}
                    />
                  ))}
                </div>
              </>
            )}
            <PhoneLabel className="mb-2 mt-3.5">From today · nearest stop</PhoneLabel>
            <PhoneStrip cells={view.strip} today={view.today} selected={view.day.date} hasPlace={hasPlace}
              onPick={(date) => onDay(person, date)} />
            <div className="mt-2.5 text-[12px] text-text-muted">Tap a day to see it hour by hour.</div>
          </>
        )}
      </Body>
    </>
  )
}

/** Board 15 D. */
function SideScreen({ choice, onDay, onDone }: {
  choice: Choice
  onDay: (person: PersonRef, date: string) => void
  onDone: () => void
}) {
  const api = useRequestSheet()
  const schedule = api.schedule!
  const shown = choice.shown
  const ids = schedule.people.map((one) => one.id)
  const { view, failed } = useCompare(ids, schedule.day, schedule.first)

  return (
    <>
      <PhoneHeader title="Side by side" onBack={api.closeSchedule} onDone={onDone} />
      <Body>
        {!view && <Quiet failed={failed} />}
        {view && (
          <>
            {view.estimates && api.facts.place && <div className="mb-3"><EstimatesBand /></div>}
            <PhoneLabel className="mb-2">Each person’s best time</PhoneLabel>
            <div className="flex flex-col gap-2">
              {view.people.map((column) => (
                <PersonBestCard
                  key={column.person.id}
                  person={refOf(column.person)}
                  opening={column.best}
                  phone
                  selected={Boolean(column.best && shown && shown.personId === column.person.id
                    && shown.date === column.best.date && shown.start === column.best.start)}
                  notCovering={notCoveringWords(column.person.covers, api.facts.divisionName, api.facts.segment)}
                  onPick={() => column.best && choice.choose(refOf(column.person), timeOf(column.best))}
                />
              ))}
            </div>
            <PhoneLabel className="mb-1.5 mt-3.5">Nearest stop · swipe for more days</PhoneLabel>
            <PhoneNearnessGrid view={view} hasPlace={api.facts.place !== null}
              onCell={(person, date) => onDay(refOf(person), date)} />
            <div className="mt-2.5 text-[12px] text-text-muted">Tap a cell to see that person’s day.</div>
          </>
        )}
      </Body>
    </>
  )
}

/** Board 16 A. */
function DayScreen({ choice, person, date, onBack, onDone }: {
  choice: Choice
  person: PersonRef
  date: string
  onBack: () => void
  onDone: () => void
}) {
  const api = useRequestSheet()
  const { view, failed } = useCalendar(person.id, date, null)
  const [mapOpen, setMapOpen] = useState(false)
  const shown = choice.shown
  const plan = view?.day.plan ?? null
  const chosenStart = shown && plan && shown.personId === person.id && shown.date === plan.date ? shown.start : null
  const placement = useMemo(() => (plan && chosenStart !== null
    ? placementOf(plan, chosenStart, api.facts.minutes) : null), [plan, chosenStart, api.facts.minutes])

  /* The day's best opening, for the chip: Best times first, else the day's best gap. */
  const best = useMemo(() => {
    if (!view || !plan) return null
    const listed = view.best.find((opening) => opening.date === plan.date)
    if (listed) return { start: listed.start, added: listed.added, grade: listed.grade }
    const fits = gapsOf(plan, view.minutes).filter((gap) => gap.fits && gap.grade && gap.earliest !== null
      && gap.added !== null)
    const top = [...fits].sort((a, b) => compareOpenings(
      { grade: a.grade, added: a.added, date: plan.date, earliest: a.earliest },
      { grade: b.grade, added: b.added, date: plan.date, earliest: b.earliest }))[0]
    return top ? { start: top.earliest!, added: top.added!, grade: top.grade! } : null
  }, [view, plan])

  const timeFor = (start: number): ChosenTime =>
    ({ date, start, startsAt: instantOf(date, start, view?.person.timeZone ?? api.setup.timeZone) })

  return (
    <>
      <PhoneHeader title={dayShort(date)} onBack={onBack} onDone={onDone} />
      <Body padded="px-3.5 py-3">
        {!view && <Quiet failed={failed} />}
        {view && plan && (
          <>
            <div className="flex items-center gap-1.5">
              {best && (
                <button type="button" onClick={() => choice.choose(person, timeFor(best.start))}
                  className={cn('inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-chip border',
                    'border-accent-line bg-accent-soft px-2.5 text-[12.5px] font-semibold text-accent-dark')}>
                  {GRADE_LABELS[best.grade]} · {clock(best.start)} · +{best.added} min
                </button>
              )}
              <span className="flex-1" />
              <button type="button" aria-label={mapOpen ? 'Hide the map' : 'Show the map'} aria-pressed={mapOpen}
                onClick={() => setMapOpen((open) => !open)}
                className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-button border border-line bg-surface text-text">
                <PinGlyph size={15} stroke={2} />
              </button>
            </div>
            <div className="h-2.5" />
            {mapOpen && (
              <div className="mb-2.5">
                <DayMap day={view.day} plan={plan} gap={placement?.gap ?? null} width="100%" height={200} />
              </div>
            )}
            {placement && shown && placement.state !== 'ok' && (
              <div className="mb-2.5">
                <PlacementBand placement={placement} plan={plan} person={view.person} shown={shown}
                  onMove={(start) => choice.move(person, timeFor(start))} />
              </div>
            )}
            <DayTimeline
              plan={plan}
              person={view.person}
              minutes={view.minutes}
              chosenStart={chosenStart}
              axis={axisOf([plan])}
              words={blockWords(api.facts)}
              onPick={(start) => choice.choose(person, timeFor(start))}
              onMove={(start) => choice.move(person, timeFor(start))}
            />
          </>
        )}
      </Body>
    </>
  )
}
